import { useEffect, useState, type ReactNode } from 'react';
import type { AuthedUser } from '../lib/auth-types';
import { I } from '../lib/icons';
import { AvatarMenu } from '../components/AvatarMenu';
import { TransferListPanel } from './TransferListPanel';
import { RotatingBackground } from './RotatingBackground';

/**
 * TransferShell — authed Kopfzeile der Transfer-App.
 *
 * Nav-Umbau 2026-07-16 (Runde 2, User-Feedback): Konto und Verwaltung getrennt.
 * Der frühere Sammel-Burger mischte Email/Abmelden (Konto) mit der „Meine
 * Transfers"-Liste (Verwaltung). Jetzt:
 *  - links Logo + „Transfer" (unverändert),
 *  - rechts ein „Meine Transfers"-Textlink, der eine rechte Drawer mit der Liste
 *    öffnet, daneben das AvatarMenu (Initialen-Avatar → Email-Header + Abmelden).
 *
 * AvatarMenu ist die geteilte, WAI-ARIA-konforme Konto-Komponente (Roving-Focus/
 * Escape/Outside-Click). Custom items = NUR Abmelden (kein „Profil" — der
 * Transfer-Client hat keine /settings-Route), header = Email. Die Drawer ist ein
 * leichtgewichtiges Overlay (Backdrop-Fade + slide-in-Panel, Escape/Backdrop-
 * Click schließen), kein Focus-Trap — genügt für die schlanke Single-Page-App.
 *
 * Der Hintergrund (GF-Muster + Weiß-Scrim) sitzt am Root; die weiße Nav und die
 * zentrierte Sende-Karte floaten darüber.
 */
export function TransferShell({
  user,
  onLogout,
  children,
}: {
  user: AuthedUser;
  onLogout: () => void;
  children: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Escape schließt die Drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-brand-green-900">
      <header className="border-b border-brand-green-900/10 bg-brand-yellow">
        {/* 3-Spalten-Nav: leerer Spacer links = Aktionen rechts (beide flex-1)
            → Logo/„Transfer" exakt mittig, „Meine Transfers"/Avatar bleiben rechts. */}
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-3">
          <div className="flex-1" aria-hidden="true" />
          <div className="flex items-center gap-2.5">
            {/* Grüne GREENFORCE-Wortmarke (eigenes File, NICHT das globale
                greenforce-logo.png — das sitzt auf der grünen DAM-Sidebar und
                wäre grün-auf-grün unsichtbar). Auf der gelben Nav = klarer Kontrast. */}
            <img
              src="/greenforce-logo-green.png"
              alt="GREENFORCE"
              className="h-9 w-auto select-none"
            />
            {/* Gesperrte Versalien (uppercase via CSS → Textinhalt bleibt
                „Transfer"), eine Spur kleiner → passt besser zur Wortmarke. */}
            <span className="text-sm font-bold uppercase tracking-widest text-brand-green-700">
              Transfer
            </span>
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="rounded px-1.5 py-1 text-sm font-medium text-brand-green-700 hover:text-brand-green-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
            >
              Meine Transfers
            </button>
            {/* Konto-Menü (geteilte AvatarMenu): Email-Header + Abmelden. Kein
                „Profil" — der Transfer-Client routet nicht auf /settings. */}
            <AvatarMenu
              user={user}
              onLogout={onLogout}
              header={user.email}
              items={[
                {
                  kind: 'action',
                  id: 'logout',
                  label: 'Abmelden',
                  onClick: onLogout,
                  tone: 'danger',
                },
              ]}
            />
          </div>
        </div>
      </header>

      {/* Main = rotierender Foto-Hintergrund (unter der Nav) + Content darüber.
          Der z-10-Wrapper ist flex-col; TransferPage-Root trägt my-auto → Karte
          vertikal zentriert (clip-sicher), links (WeTransfer). overflow-y-auto
          fängt hohen Inhalt ab, der Hintergrund bleibt fix (absolute zu main). */}
      <main className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <RotatingBackground />
        {/* Scroll-Container: 100dvh-fix (Root overflow-hidden) → bei zu viel
            Karten-Inhalt (lange Datei-Queue) scrollt NUR dieser Bereich, nicht
            die Seite. my-auto (statt justify-center) auf der Karte zentriert
            clip-sicher: mittig bei Platz, oben bei Überlauf (kein oberes Clipping). */}
        <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
          {children}
        </div>
      </main>

      {drawerOpen && (
        <>
          {/* Backdrop (Fade) — Click schließt. */}
          <div
            className="gf-modal-backdrop fixed inset-0 z-40 bg-black/30"
            aria-hidden="true"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Rechte „Meine Transfers"-Drawer (slide-in). Der Titel kommt aus der
              Liste selbst (TransferListPanel rendert h2 „Meine Transfers"), die
              Drawer-Kopfzeile trägt nur den Schließen-Button — keine Doppelung. */}
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Meine Transfers"
            className="gf-drawer-panel fixed inset-y-0 right-0 z-50 flex w-[min(92vw,440px)] flex-col bg-white shadow-pop"
          >
            <div className="flex items-center justify-end border-b border-ink-100 px-4 py-3">
              <button
                type="button"
                aria-label="Schließen"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-brand-green-700 hover:bg-brand-green-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
              >
                <I.close size={20} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <TransferListPanel authedUser={user} />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
